import { useCallback, useEffect, useMemo, useState } from "react";

import type { ReviewCycleReportRowFrontend } from "@performyard/service-contracts";

type UseReviewFormsSelectionProps = {
	/** Every row in the cycle, before the search filter */
	allRows: ReviewCycleReportRowFrontend[];
	/** The rows currently visible in the table, after the search filter */
	filteredRows: ReviewCycleReportRowFrontend[];
};

/**
 * Row selection for the Review Forms table.
 *
 * Selection is two-tier, matching the table's search behavior:
 * - the header checkbox selects or clears the rows currently visible in the table
 * - "Select all N forms" selects every form in the cycle, including rows the search hides
 *
 * Ids that leave the row set (deleted elsewhere, or dropped by a refetch) are pruned so the
 * selected count can never exceed what's actually in the table.
 */
export const useReviewFormsSelection = ({ allRows, filteredRows }: UseReviewFormsSelectionProps) => {
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

	const allRowIds = useMemo(() => allRows.map((row) => row._id), [allRows]);

	// Prune ids that no longer exist in the row set
	useEffect(() => {
		setSelectedIds((previous) => {
			if (previous.size === 0) return previous;
			const available = new Set(allRowIds);
			const pruned = new Set([...previous].filter((id) => available.has(id)));
			return pruned.size === previous.size ? previous : pruned;
		});
	}, [allRowIds]);

	const toggleRow = useCallback((rowId: string, selected: boolean) => {
		setSelectedIds((previous) => {
			const next = new Set(previous);
			if (selected) {
				next.add(rowId);
			} else {
				next.delete(rowId);
			}
			return next;
		});
	}, []);

	/** Header checkbox: select or clear only the rows the search is currently showing */
	const setFilteredRowsSelected = useCallback(
		(selected: boolean) => {
			setSelectedIds((previous) => {
				const next = new Set(previous);
				for (const row of filteredRows) {
					if (selected) {
						next.add(row._id);
					} else {
						next.delete(row._id);
					}
				}
				return next;
			});
		},
		[filteredRows]
	);

	/** Banner link: select every form in the cycle, ignoring the search filter */
	const selectAllRows = useCallback(() => {
		setSelectedIds(new Set(allRowIds));
	}, [allRowIds]);

	const clearSelection = useCallback(() => {
		setSelectedIds(new Set());
	}, []);

	const allFilteredRowsSelected = useMemo(
		() => filteredRows.length > 0 && filteredRows.every((row) => selectedIds.has(row._id)),
		[filteredRows, selectedIds]
	);

	const allRowsSelected = useMemo(
		() => allRowIds.length > 0 && selectedIds.size === allRowIds.length,
		[allRowIds, selectedIds]
	);

	return {
		selectedIds,
		selectedCount: selectedIds.size,
		totalCount: allRowIds.length,
		isRowSelected: useCallback((rowId: string) => selectedIds.has(rowId), [selectedIds]),
		toggleRow,
		setFilteredRowsSelected,
		selectAllRows,
		clearSelection,
		allFilteredRowsSelected,
		allRowsSelected,
	};
};
